import { useContext, useState } from 'react';
import { Box, Button, Grid, Typography, Divider } from '@mui/material';
import {
    AccountCircleOutlined,
    CalendarTodayOutlined,
    CheckCircle,
    CreditCard,
    Refresh,
    LocationOnOutlined
} from '@mui/icons-material';
// Our Components
import { AuthContext } from 'context/auth-context';
import { CARD_TYPE, REPLENISH_FRECUENCY, GIFT_CARD_CATEGORY } from 'helpers/constants';
import PaystandCard from './PaystandCard/PaystandCard';
// Utils and Helpers
import { hasCreditLine, hasCreditModel } from 'helpers/businesses';
import { numberFormat } from 'helpers/i18n';
import dayjs from 'helpers/dayjs';
// Assets
import physicalCardBackside from 'assets/cardsSection/cardBackgroud.svg';
import virtualCardBackside from 'assets/createCards/virtualCardBackside.png';
import './CardOverview.css';

const CardOverview = ({ createdCard, card, cardholder, onDoneClick }) => {
    const { businessInfo } = useContext(AuthContext);
    const [showCardInfo, setShowCardInfo] = useState(false);

    const getReplenishFrequency = replenishFrequency => {
        return replenishFrequency === REPLENISH_FRECUENCY.ONE_TIME ? 'Manual' : 'Automatic';
    };

    const getBusinessAddress = () => {
        const { address } = businessInfo;
        const lineTwo = !address.line2 ? '' : `${address.line2}, `;
        return `${address.line1}, ${lineTwo}${address.city}, ${address.state} ${address.postalCode}`;
    };

    const getTitleText = () => {
        if (card.type === CARD_TYPE.PHYSICAL && card.label === GIFT_CARD_CATEGORY) {
            return `Your order for ${GIFT_CARD_CATEGORY} is successful`;
        }
        if (card.type === CARD_TYPE.PHYSICAL) {
            return 'Your request was sent successfully.';
        }
        return `Your request was sent successfully.`;
    };

    const cardLabel = card.label === GIFT_CARD_CATEGORY ? GIFT_CARD_CATEGORY : 'card';

    return (
        <Box className="request-card" sx={{ width: '100%' }}>
            <Grid container columnSpacing={{ md: 3 }}>
                <Grid item md={6}>
                    <Box>
                        <Box className="request-card-overview__icon-container" sx={{ marginBottom: '16px' }}>
                            <CheckCircle sx={{ color: '#429E00', fontSize: '35px' }} />
                        </Box>
                        <Typography className="request-card-overview__congrats">
                            Congratulations, {cardholder?.first_name}!
                        </Typography>
                        <Typography className="request-card-overview__title">{getTitleText()}</Typography>
                        {card.type === CARD_TYPE.PHYSICAL && (
                            <>
                                <Typography className="request-card-overview__delivery-time" marginBottom="25px">
                                    Once the admin approves your request, your physical card will be delivered to the below
                                    address in 5-7 business days. We will send you the UPS tracking number via email once the card
                                    is shipped.
                                </Typography>
                                <Box display="flex" alignItems="center">
                                    <LocationOnOutlined sx={{ marginRight: '14px' }} />
                                    <Typography component="span" className="request-card-overview__info">
                                        {getBusinessAddress()}
                                    </Typography>
                                </Box>
                            </>
                        )}
                        <Divider sx={{ marginTop: '15px', marginBottom: '15px' }} />
                        <Box sx={{ marginBottom: '35px' }}>
                            <Box display="flex" alignItems="center" marginBottom="16px">
                                <AccountCircleOutlined sx={{ marginRight: '14px' }} />
                                <Typography component="span" className="request-card-overview__info">
                                    Cardholder: {`${cardholder?.first_name} ${cardholder?.last_name}`}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" marginBottom="16px">
                                <CreditCard sx={{ marginRight: '14px' }} />
                                <Typography component="span" className="request-card-overview__info">
                                    {hasCreditModel(businessInfo) && hasCreditLine(businessInfo)
                                        ? `Limit: ${numberFormat(card.limitAmount)}`
                                        : `Card Balance: ${numberFormat(card.limitAmount)}`}
                                </Typography>
                            </Box>
                            {hasCreditModel(businessInfo) && hasCreditLine(businessInfo) && (
                                <Box display="flex" alignItems="center" marginBottom="16px">
                                    <Refresh sx={{ marginRight: '14px' }} />
                                    <Typography component="span" className="request-card-overview__info">
                                        Replenishment: {getReplenishFrequency(card.has_manual_replenish)}
                                    </Typography>
                                </Box>
                            )}
                            <Box display="flex" alignItems="center" marginBottom="35px">
                                <CalendarTodayOutlined sx={{ marginRight: '14px' }} />
                                <Typography component="span" className="request-card-overview__info">
                                    Lock Date: {card.autoLock ? dayjs(card.autoLock).format('DD/MM/YYYY') : 'N/A'}
                                </Typography>
                            </Box>
                        </Box>
                        <Button variant="contained" onClick={onDoneClick} sx={{ width: '100%' }}>
                            Done
                        </Button>
                    </Box>
                </Grid>
                <Grid item md={6}>
                    {(card.type === CARD_TYPE.PHYSICAL || cardLabel === GIFT_CARD_CATEGORY) && (
                        <Box
                            className="request-card__card-banner-container"
                            sx={{
                                background: 'linear-gradient(to bottom, rgba(27, 171, 226, 0.2) 50%, #f9f9f8 50%)'
                            }}>
                            <Box className="request-card__card-image-container-overview" sx={{ position: 'relative' }}>
                                <img
                                    className="request-card__card-image"
                                    src={card.type === CARD_TYPE.VIRTUAL ? virtualCardBackside : physicalCardBackside}
                                    alt="virtual card"
                                />
                                <Typography component="div" className="request-card__card-secret request-card__card-number">
                                    **** **** **** ****
                                    <span
                                        style={{
                                            position: 'absolute',
                                            top: -2,
                                            left: 90,
                                            fontSize: '14px'
                                        }}></span>
                                </Typography>
                                <Typography component="div" className="request-card__card-secret request-card__card-expiration">
                                    **/**
                                </Typography>
                                <Typography component="div" className="request-card__card-secret request-card__card-cvv">
                                    ***
                                </Typography>

                                <Typography
                                    component="div"
                                    className="request-card__card-backside-cardholder-name embossing-ellipsis"
                                    sx={{
                                        color: `${card.type === CARD_TYPE.VIRTUAL ? 'var(--white-color)' : 'var(--main-color)'}`
                                    }}>
                                    {`${cardholder?.first_name} ${cardholder?.last_name}`}
                                </Typography>
                                <Typography
                                    component="div"
                                    className="request-card__card-backside-cardholder-company embossing-ellipsis"
                                    sx={{
                                        color: `${card.type === CARD_TYPE.VIRTUAL ? 'var(--white-color)' : 'var(--main-color)'}`
                                    }}>
                                    {businessInfo.legal_name}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {card.type === CARD_TYPE.VIRTUAL && cardLabel !== GIFT_CARD_CATEGORY && (
                        <Box
                            className="request-card__card-banner-container"
                            sx={{
                                background: 'linear-gradient(to bottom, rgba(27, 171, 226, 0.2) 50%, #f9f9f8 50%)',
                                flexDirection: 'column'
                            }}>
                            <Box className="request-card__card-image-container-overview" marginBottom="0px">
                                <PaystandCard card={createdCard} showCardInfo={showCardInfo} setShowCardInfo={setShowCardInfo} />
                            </Box>
                        </Box>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default CardOverview;
